import { useState } from "react";
import { Link } from "react-router";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  User,
  Calendar,
  MapPin,
  Briefcase,
  Globe,
  CheckCircle2,
  Clock,
  MessageSquare,
  ExternalLink,
  HeartHandshake,
  AtSign,
  ClipboardCheck,
} from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  THERMOMETER_CONFIG,
  type ActivityDetail,
  type ActivityType,
} from "@/types/activity";
import { CompletarAtividadeModal } from "./CompletarAtividadeModal";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { trackFreelancerView } from "@/hooks/use-track-freelancer-view";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { FreelancerLinkedInButton } from "@/components/freelancers/painel/FreelancerLinkedInButton";

interface UserProfile {
  id: string;
  full_name?: string;
  username?: string;
}

interface ActivityCardProps {
  activity: ActivityDetail;
  showCompletedInfo?: boolean;
}

// Configuracao de estilos por tipo de atividade
const TYPE_STYLES: Record<
  ActivityType,
  {
    borderColor: string;
    badgeClass: string;
    icon: React.ComponentType<{ className?: string }>;
    iconColor: string;
    iconBg: string;
    label: string;
  }
> = {
  falar_com_frila: {
    borderColor: "border-l-violet-500",
    badgeClass: "bg-violet-100 text-violet-700 border-violet-200",
    icon: HeartHandshake,
    iconColor: "text-violet-600",
    iconBg: "bg-violet-50",
    label: "Fazer Amizade",
  },
  mencao: {
    borderColor: "border-l-blue-500",
    badgeClass: "bg-blue-100 text-blue-700 border-blue-200",
    icon: AtSign,
    iconColor: "text-blue-600",
    iconBg: "bg-blue-50",
    label: "Mencao",
  },
  revisao_cadastro: {
    borderColor: "border-l-amber-500",
    badgeClass: "bg-amber-100 text-amber-700 border-amber-200",
    icon: ClipboardCheck,
    iconColor: "text-amber-600",
    iconBg: "bg-amber-50",
    label: "Revisao",
  },
};

export function ActivityCard({
  activity,
  showCompletedInfo = false,
}: ActivityCardProps) {
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Buscar usuarios para renderizar mencoes
  const { data: usuarios } = useQuery({
    queryKey: ["usuarios"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, username");
      if (error) throw error;
      return data as UserProfile[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });


  const isPending = activity.status === "pending";
  const isMencao = activity.tipo === "mencao";
  const style = TYPE_STYLES[activity.tipo];
  const IconComponent = style.icon;

  // Renderizar mensagem com mencoes destacadas (baseado em usuarios conhecidos)
  const renderMensagemComMencoes = (mensagem: string) => {
    if (!usuarios || usuarios.length === 0) {
      return mensagem;
    }

    // Criar lista de nomes de usuarios para buscar
    const userNames = usuarios
      .map((u) => u.full_name || u.username)
      .filter((name): name is string => !!name)
      .sort((a, b) => b.length - a.length); // Ordenar por tamanho desc

    // Criar regex que busca @nome para cada usuario
    const escapedNames = userNames.map((name) =>
      name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );
    const mentionRegex = new RegExp(`(@(?:${escapedNames.join("|")}))`, "gi");

    const parts = mensagem.split(mentionRegex);

    return parts.map((part, index) => {
      if (part.startsWith("@")) {
        const mentionName = part.slice(1);
        const isValidUser = userNames.some(
          (name) => name.toLowerCase() === mentionName.toLowerCase()
        );
        if (isValidUser) {
          return (
            <span
              key={index}
              className="bg-blue-100 text-blue-700 px-1 rounded font-medium"
            >
              {part}
            </span>
          );
        }
      }
      return part;
    });
  };

  // Marca a mencao como concluida ao abrir a vaga. A navegacao em si fica a
  // cargo do <Link> (clique simples), mantendo cmd/clique do meio = nova aba.
  const handleGoToVaga = async () => {
    if (activity.status === "pending") {
      await supabase.rpc("complete_freelancer_activity", {
        p_activity_id: activity.id,
      });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["activity_pending_count"] });
    }
  };

  return (
    <>
      <Card
        className={cn(
          "hover:shadow-md transition-all border-l-4",
          style.borderColor
        )}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Icone do tipo */}
            <div className={cn("p-2 rounded-lg", style.iconBg)}>
              <IconComponent className={cn("h-5 w-5", style.iconColor)} />
            </div>

            {/* Conteudo */}
            <div className="flex-1 min-w-0 space-y-2">
              {/* Header com nome e badges */}
              <div className="flex items-center gap-2 flex-wrap">
                {isMencao ? (
                  <span className="font-semibold text-base">
                    Vaga: {activity.vaga_titulo || "Sem titulo"}
                  </span>
                ) : (
                  <span className="font-semibold text-base">
                    {activity.freelancer_nome}
                  </span>
                )}

                <Badge className={cn("text-xs border", style.badgeClass)}>
                  {style.label}
                </Badge>

                {isPending ? (
                  <Badge
                    variant="outline"
                    className="text-xs text-orange-600 border-orange-300"
                  >
                    <Clock className="h-3 w-3 mr-1" />
                    Pendente
                  </Badge>
                ) : (
                  <Badge className="text-xs bg-green-100 text-green-700 border border-green-200">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Concluida
                  </Badge>
                )}
              </div>

              {/* Mensagem da mencao */}
              {isMencao && activity.observacao_mensagem && (
                <div className="text-sm bg-blue-50 border border-blue-100 p-3 rounded-md">
                  <div className="flex items-start gap-2">
                    <MessageSquare className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                    <div className="text-slate-700">
                      {renderMensagemComMencoes(activity.observacao_mensagem)}
                    </div>
                  </div>
                </div>
              )}

              {/* Areas (apenas para atividades de freelancer) */}
              {!isMencao &&
                activity.freelancer_areas &&
                activity.freelancer_areas.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Briefcase className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {activity.freelancer_areas.join(", ")}
                    </span>
                  </div>
                )}

              {/* Cidade (apenas para atividades de freelancer) */}
              {!isMencao && activity.freelancer_cidade && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span>{activity.freelancer_cidade}</span>
                </div>
              )}

              {/* Atribuido e Criado */}
              <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                {activity.assigned_to_name && (
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    <span>Atribuido: {activity.assigned_to_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>
                    Criado em{" "}
                    {format(new Date(activity.created_at), "dd/MM/yyyy", {
                      locale: ptBR,
                    })}
                  </span>
                </div>
                {activity.created_by_name && (
                  <span className="text-muted-foreground/70">
                    por {activity.created_by_name}
                  </span>
                )}
              </div>

              {/* Observacao (nao exibir para mencoes, pois ja mostramos a mensagem) */}
              {activity.observacao && !isMencao && (
                <div className="text-sm bg-muted/50 p-2 rounded mt-2">
                  <span className="text-muted-foreground">Obs: </span>
                  {activity.observacao}
                </div>
              )}

              {/* Info de conclusao */}
              {showCompletedInfo && !isPending && (
                <div className="mt-3 pt-3 border-t space-y-2">
                  {/* Termometro */}
                  {activity.termometro && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Termometro:
                      </span>
                      <span className="text-lg">
                        {THERMOMETER_CONFIG[activity.termometro].emoji}
                      </span>
                      <span className="text-sm font-medium">
                        {THERMOMETER_CONFIG[activity.termometro].label}
                      </span>
                    </div>
                  )}

                  {/* Sentimento */}
                  {activity.sentimento && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Sentimento: </span>
                      {activity.sentimento}
                    </div>
                  )}

                  {/* Resumo */}
                  {activity.resumo && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Resumo: </span>
                      {activity.resumo}
                    </div>
                  )}

                  {/* Conversa WhatsApp */}
                  {activity.conversa_whatsapp && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 h-7 px-2"
                          >
                            <MessageSquare className="h-3 w-3" />
                            Ver conversa
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="max-w-md max-h-60 overflow-auto"
                        >
                          <pre className="text-xs whitespace-pre-wrap font-mono">
                            {activity.conversa_whatsapp}
                          </pre>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}

                  {/* Data e responsavel pela conclusao */}
                  {activity.completed_at && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-3 w-3 text-green-600" />
                      <span>
                        Concluida em{" "}
                        {format(
                          new Date(activity.completed_at),
                          "dd/MM/yyyy 'as' HH:mm",
                          {
                            locale: ptBR,
                          }
                        )}
                      </span>
                      {activity.completed_by_name && (
                        <span>por {activity.completed_by_name}</span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Acoes */}
            <div className="flex flex-col gap-2 shrink-0">
              {/* Links externos (apenas para atividades de freelancer) */}
              {!isMencao && (
                <div className="flex gap-1">
                  <ExternalLinkButton
                    kind="portfolio"
                    url={activity.freelancer_portfolio_url}
                    className="hover:bg-slate-100"
                  />
                  <FreelancerLinkedInButton
                    freelancerId={activity.freelancer_id}
                    url={activity.freelancer_linkedin_url}
                    className="hover:bg-blue-100 hover:text-blue-600"
                  />
                </div>
              )}

              {/* Botao para ir a vaga (para mencoes) */}
              {isMencao && activity.vaga_id && (
                <Button asChild variant="outline" size="sm" className="gap-1">
                  <Link
                    to={`/vagas/${activity.vaga_id}?tab=observacoes`}
                    onClick={handleGoToVaga}
                  >
                    <ExternalLink className="h-4 w-4" />
                    Ver Observacao
                  </Link>
                </Button>
              )}

              {/* Botao para ver freelancer (para revisao de cadastro) */}
              {activity.tipo === "revisao_cadastro" && activity.freelancer_id && (
                <Button asChild variant="outline" size="sm" className="gap-1">
                  <Link
                    to={`/freelancers/${activity.freelancer_id}`}
                    onClick={() =>
                      trackFreelancerView(activity.freelancer_id, "atividade")
                    }
                  >
                    <ExternalLink className="h-4 w-4" />
                    Ver Freelancer
                  </Link>
                </Button>
              )}

              {/* Botao de completar (apenas para atividades de freelancer) */}
              {isPending && !isMencao && (
                <Button
                  size="sm"
                  onClick={() => setIsCompleteModalOpen(true)}
                  className="gap-1"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Completar
                </Button>
              )}

              {/* Botao de marcar como concluida (para mencoes pendentes) */}
              {isPending && isMencao && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={async () => {
                    await supabase.rpc("complete_freelancer_activity", {
                      p_activity_id: activity.id,
                    });
                    queryClient.invalidateQueries({
                      queryKey: ["activities"],
                    });
                    queryClient.invalidateQueries({
                      queryKey: ["activity_pending_count"],
                    });
                  }}
                  className="gap-1"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Marcar Concluida
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal de completar atividade */}
      <CompletarAtividadeModal
        isOpen={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
        activity={activity}
      />
    </>
  );
}
